<template>
  <div class="feedback-form">
    <div class="feedback-header" @click="isExpanded = !isExpanded">
      <span class="feedback-title">
        <i class="fas fa-comment-alt"></i>
        Geef feedback
      </span>
      <i :class="['fas', isExpanded ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
    </div>
    
    <div v-if="isExpanded" class="feedback-content">
      <div v-if="submitSuccess" class="alert alert-success">
        Bedankt voor uw feedback!
      </div>
      
      <div v-else>
        <textarea 
          v-model="feedback" 
          placeholder="Uw opmerking..."
          rows="3"
          class="form-control"
          :disabled="isSubmitting"
        ></textarea>
        
        <div class="captcha-container">
          <SReCaptcha 
            ref="recaptcha"
            @onRecaptchaTokenResponse="onCaptchaVerified" 
          />
        </div>
        
        <div v-if="errorMessage" class="alert alert-danger">
          {{ errorMessage }}
        </div>
        
        <button 
          @click="submitFeedback" 
          :disabled="!canSubmit || isSubmitting"
          class="btn btn-primary btn-sm"
        >
          <span v-if="isSubmitting">
            <i class="fas fa-spinner fa-spin"></i> Verzenden...
          </span>
          <span v-else>
            <i class="fas fa-paper-plane"></i> Verstuur
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import SReCaptcha from '../SReCaptcha.vue';
import ServiceFactory from '@/services/ServiceFactory';

export default defineComponent({
  name: 'FeedbackForm',
  
  components: {
    SReCaptcha
  },
  
  props: {
    formId: {
      type: String,
      required: true
    },
    recordId: {
      type: String,
      required: true
    },
    targetField: {
      type: String,
      required: true
    }
  },
  
  setup(props) {
    const feedback = ref('');
    const recaptchaToken = ref('');
    const isSubmitting = ref(false);
    const submitSuccess = ref(false);
    const errorMessage = ref('');
    const isExpanded = ref(false);
    const recaptcha = ref<InstanceType<typeof SReCaptcha> | null>(null);
    
    const canSubmit = computed(() => {
      return feedback.value.trim().length > 0 && recaptchaToken.value.length > 0;
    });
    
    const onCaptchaVerified = (token: string) => {
      recaptchaToken.value = token;
    };
    
    const submitFeedback = async () => {
      if (!canSubmit.value) return;
      
      isSubmitting.value = true;
      errorMessage.value = '';
      
      try {
        const service = ServiceFactory.getDataService();
        await service.submitFeedback(
          props.formId,
          props.recordId,
          props.targetField,
          feedback.value,
          recaptchaToken.value
        );
        
        submitSuccess.value = true;
        feedback.value = '';
        recaptchaToken.value = '';
        
        // Reset na 3 seconden
        setTimeout(() => {
          submitSuccess.value = false;
          isExpanded.value = false;
          recaptcha.value?.reset();
        }, 3000);
      } catch (error: any) {
        errorMessage.value = error.message || 'Er is een fout opgetreden. Probeer het opnieuw.';
        recaptcha.value?.reset();
        recaptchaToken.value = '';
      } finally {
        isSubmitting.value = false;
      }
    };
    
    return {
      feedback,
      recaptchaToken,
      isSubmitting,
      submitSuccess,
      errorMessage,
      isExpanded,
      canSubmit,
      onCaptchaVerified,
      submitFeedback,
      recaptcha
    };
  }
});
</script>

<style scoped>
.feedback-form {
  border-top: 1px solid #dee2e6;
  margin-top: 15px;
  padding-top: 10px;
}

.feedback-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
  color: var(--filter-btn-color, #007bff);
}

.feedback-header:hover {
  opacity: 0.8;
}

.feedback-title {
  font-weight: 500;
  font-size: 0.9rem;
}

.feedback-title i {
  margin-right: 8px;
}

.feedback-content {
  padding: 10px 0;
}

.feedback-content textarea {
  width: 100%;
  resize: vertical;
  font-size: 0.9rem;
}

.captcha-container {
  margin: 10px 0;
  transform: scale(0.85);
  transform-origin: left top;
}

.btn-primary {
  background-color: var(--filter-btn-color, #007bff);
  border-color: var(--filter-btn-color, #007bff);
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--filter-btn-hover-bg-color, #0056b3);
  border-color: var(--filter-btn-hover-bg-color, #0056b3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.alert {
  padding: 8px 12px;
  border-radius: 4px;
  margin: 10px 0;
  font-size: 0.85rem;
}

.alert-success {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}
</style>
